import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent } from '@mui/material';
import { CircularProgress as MuiCircularProgress, CircularProgressProps } from '@mui/material';
import { Business, Person, CheckCircle, Warning, Error } from '@mui/icons-material';

interface CircularDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

// Category name mappings with icons
const categoryNames: Record<string, { hebrew: string; english: string; icon: string }> = {
  'management': { hebrew: 'ניהול', english: 'Management', icon: '🎯' },
  'organization': { hebrew: 'ארגון', english: 'Organization', icon: '🏢' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales', icon: '🔄' },
  'finance': { hebrew: 'כספים', english: 'Finance', icon: '💰' },
  'operations': { hebrew: 'תפעול', english: 'Operations', icon: '⚙️' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control', icon: '✅' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales', icon: '📈' },
  'stress': { hebrew: 'מתח', english: 'Stress', icon: '😰' },
  'principles': { hebrew: 'עקרונות', english: 'Principles', icon: '🧭' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help', icon: '🤝' },
};

const CircularProgressWithLabel: React.FC<CircularProgressProps & { value: number; size?: number; label: string; icon: string }> = ({
  value,
  size = 120,
  label,
  icon,
  ...props
}) => {
  const getColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {/* Background circle */}
      <MuiCircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{
          color: '#e0e0e0',
          position: 'absolute',
          left: 0
        }}
      />
      {/* Progress circle */}
      <MuiCircularProgress
        variant="determinate"
        value={value}
        size={size}
        thickness={4}
        sx={{
          color: getColor(value),
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round'
          }
        }}
        {...props}
      />
      {/* Center content */}
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}
      >
        <Typography variant="h4" component="div" sx={{ fontSize: size * 0.25 }}>
          {icon}
        </Typography>
        <Typography variant="h6" component="div" fontWeight="bold" sx={{ fontSize: size * 0.16 }}>
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

const CircularDashboard: React.FC<CircularDashboardProps> = ({ categoryScores, language }) => {
  // Process category scores
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he'
          ? categoryNames[categoryKey]?.hebrew
          : categoryNames[categoryKey]?.english,
        icon: categoryNames[categoryKey]?.icon || '📊',
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
      overallAverage: scores.reduce((sum, cat) => sum + cat.value, 0) / scores.length
    };
  }, [categoryScores, language]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  const getStatusIcon = (score: number) => {
    if (score >= 80) return <CheckCircle sx={{ color: '#27ae60', fontSize: 24 }} />;
    if (score >= 60) return <CheckCircle sx={{ color: '#2ecc71', fontSize: 24 }} />;
    if (score >= 40) return <Warning sx={{ color: '#f39c12', fontSize: 24 }} />;
    return <Error sx={{ color: '#e74c3c', fontSize: 24 }} />;
  };

  const renderCategoryCircle = (category: any) => (
    <Grid item xs={6} sm={4} md={3} key={category.key}>
      <Card
        sx={{
          height: '100%',
          textAlign: 'center',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: 6
          }
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <CircularProgressWithLabel
              value={category.value}
              size={100}
              label={category.name || ''}
              icon={category.icon}
            />
          </Box>
          <Typography variant="body2" fontWeight="600" sx={{ mt: 1, mb: 0.5 }}>
            {category.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            {getStatusIcon(category.value)}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderMasterGauge = (title: string, icon: React.ReactNode, score: number, categories: any[]) => (
    <Paper
      elevation={6}
      sx={{
        p: 4,
        textAlign: 'center',
        background: `linear-gradient(135deg, ${getScoreColor(score)}15 0%, ${getScoreColor(score)}30 100%)`,
        border: `3px solid ${getScoreColor(score)}`,
        borderRadius: 4
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
        {icon}
        <Typography variant="h4" fontWeight="bold">
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {/* Background circle */}
          <MuiCircularProgress
            variant="determinate"
            value={100}
            size={200}
            thickness={6}
            sx={{
              color: '#e0e0e0',
              position: 'absolute',
              left: 0
            }}
          />
          {/* Progress circle */}
          <MuiCircularProgress
            variant="determinate"
            value={score}
            size={200}
            thickness={6}
            sx={{
              color: getScoreColor(score),
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round'
              }
            }}
          />
          {/* Center content */}
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h2" component="div" fontWeight="bold" sx={{ color: getScoreColor(score) }}>
              {Math.round(score)}
            </Typography>
            <Typography variant="h6" component="div" color="text.secondary">
              %
            </Typography>
          </Box>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" fontWeight="500">
        {language === 'he'
          ? `מבוסס על ${categories.length} קטגוריות`
          : `Based on ${categories.length} categories`}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Master Gauges */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {renderMasterGauge(
            language === 'he' ? 'ממוצע עסקי' : 'Business Average',
            <Business sx={{ fontSize: 48, color: getScoreColor(processedScores.businessAverage) }} />,
            processedScores.businessAverage,
            processedScores.business
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderMasterGauge(
            language === 'he' ? 'ממוצע אישי' : 'Personal Average',
            <Person sx={{ fontSize: 48, color: getScoreColor(processedScores.personalAverage) }} />,
            processedScores.personalAverage,
            processedScores.personal
          )}
        </Grid>
      </Grid>

      {/* Business Categories */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ fontSize: 32 }} />
          {language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}
        </Typography>
        <Grid container spacing={2}>
          {processedScores.business.map(renderCategoryCircle)}
        </Grid>
      </Paper>

      {/* Personal Categories */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person sx={{ fontSize: 32 }} />
          {language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}
        </Typography>
        <Grid container spacing={2}>
          {processedScores.personal.map(renderCategoryCircle)}
        </Grid>
      </Paper>
    </Box>
  );
};

export default CircularDashboard;
